// @angular
import {
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  untracked,
  ChangeDetectionStrategy
} from "@angular/core";
import { Router } from "@angular/router";
// Translate
import { TranslateService, TranslateModule } from "@ngx-translate/core";
// Own services
import { UserControllerService } from "../../../services/server/UserController.service";
import { SessionDataService } from "../../../services/shared/SessionDataService.service";
import { ReloadComponentService } from "../../../services/shared/ReloadComponentService.service";
import { ToastService } from "../../../services/shared/ToastService.service";
import { TemplateService } from "../../../services/shared/TemplateService.service";
// Variables
import { LANGUAGE } from "../../../services/language/LanguageController.service";
import { MatButtonModule } from "@angular/material/button";
import { MaterialSelectComponent } from "../../material-module/material-select/material-select.component";
import { FormsModule } from "@angular/forms";
import { MatInputModule } from "@angular/material/input";
import { MatFormFieldModule } from "@angular/material/form-field";
import { NgTemplateOutlet } from "@angular/common";
import { MaterialSelectOption } from "../../material-module/MaterialInterface.type";

/**
 * Form for subscribers to edit their own data (contact details, language, password).
 * Loads with UserController.getSubscriberData and saves with updateSubscriberData.
 */
@Component({
  selector: "app-subscriber-edit",
  templateUrl: "./subscriber-edit.component.html",
  styleUrls: ["./subscriber-edit.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    NgTemplateOutlet,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    MaterialSelectComponent,
    MatButtonModule,
    TranslateModule
  ]
})
export class SubscriberEditComponent implements OnInit, OnDestroy {
  /***************************************************************************/
  // ANCHOR Variables
  /***************************************************************************/

  elseBlock: TemplateRef<any> | null = this.TemplateService.get("elseBlock");

  // Session variables
  sessionLanguage: string;
  languageSub: any;

  // Language list
  languagesList: MaterialSelectOption[] = [
    { value: LANGUAGE.ESPANOL, name: "Castellano" },
    { value: LANGUAGE.INGLES, name: "English" },
    { value: LANGUAGE.CATALAN, name: "Catalá" },
    { value: LANGUAGE.FRANCES, name: "Français" },
    { value: LANGUAGE.ITALIANO, name: "Italiano" },
    { value: LANGUAGE.GRIEGO, name: "Ελληνικά" },
    { value: LANGUAGE.PORTUGUES, name: "Português" }
  ];
  languageInitialSelection: string;
  languageUpdated: boolean = false;

  // Password
  showPassword: boolean = false;
  showPasswordRequeriments: boolean = false;
  repeatedPassword: string;

  // User
  userData: any = {
    email: null,
    language: null,
    name: null,
    password: null,
    phoneNumber: null,
    phoneNumber2: null,
    profile: null,
    surname: null,
    username: null
  };

  // Body
  body = document.body;

  /***************************************************************************/
  // ANCHOR Constructor
  /***************************************************************************/

  constructor(
    private readonly ReloadComponentService: ReloadComponentService,
    private readonly router: Router,
    private readonly SessionDataService: SessionDataService,
    private readonly TemplateService: TemplateService,
    private readonly ToastService: ToastService,
    private readonly translate: TranslateService,
    private readonly UserController: UserControllerService
  ) {}

  /***************************************************************************/
  // ANCHOR Component initialization
  /***************************************************************************/

  ngOnInit(): void {
    // Load initial values
    this.sessionLanguage = untracked(() => this.SessionDataService.language());

    this.getUserData();
  }

  /***************************************************************************/
  // ANCHOR Component destruction
  /***************************************************************************/

  ngOnDestroy(): void {}

  /***************************************************************************/
  // ANCHOR Functions
  /***************************************************************************/

  // Get user data for editing
  getUserData(): void {
    this.UserController.getSubscriberData().subscribe(response => {
      if (response["code"] === 0) {
        let userData: any = response["body"];
        userData.password = null;
        this.userData = userData;
        this.languageInitialSelection = this.userData.language;
      }
    });
  }

  // Discard unsaved changes by reloading the component
  reset(event: any): void {
    event.preventDefault();
    this.ReloadComponentService.reload();
  }

  // Save user data
  saveUserData(): void {
    if (this.userData.password && this.userData.password !== this.repeatedPassword) {
      this.ToastService.fireToast("error", this.translate.instant("passwords-not-match"));
    } else {
      let data: any = {
        username: this.userData.username,
        password: this.userData.password,
        name: this.userData.name,
        surname: this.userData.surname,
        email: this.userData.email,
        phoneNumber: this.userData.phoneNumber,
        phoneNumber2: this.userData.phoneNumber2,
        language: this.userData.language
      };

      this.UserController.updateSubscriberData(data).subscribe(response => {
        if (response["code"] === 0) {
          if (this.languageUpdated) {
            this.SessionDataService.sendLanguage(this.userData.language);
          }
          this.ToastService.fireToast("success", this.translate.instant("saved"));
        }
      });
    }
  }

  // Leave the edit form and go to the subscriber main page
  back(): void {
    // Prevents scrollbars in the subscriber interface
    this.body.style.overflow = "hidden";
    this.router.navigate(["/abonado/principal"]);
  }
}
